const historicalFields = {
  'booth_score>to_f': 'booth_score',
  'ex_whole_time_statistic>visitors_count': 'total_visitors',
  'ex_whole_time_statistic>engaged_visitors_count': 'engaged_visitors',
  'ex_whole_time_statistic>passing_visitors_count': 'passing_visitors',
  'ex_whole_time_statistic>dwell': 'dwell',
  'ex_whole_time_statistic>engaged_dwell': 'engaged_dwell'
}
const fieldName = field => historicalFields[field] || field

// Saved views from the previous reporting tool are read without changing the database.
export function readState (saved = {}, defaults = []) {
  saved ||= {}
  const state =
    saved.reporting?.version === 1
      ? saved.reporting
      : {
          version: 1,
          hidden: (saved.columnVisibility?.hiddenColIds || []).map(fieldName),
          order: (saved.columnOrder?.orderedColIds || []).map(fieldName),
          widths: Object.fromEntries(
            (saved.columnSizing?.columnSizingModel || []).map(col => [fieldName(col.colId), col.width])
          ),
          groups: (saved.rowGroup?.groupColIds || defaults).map(fieldName),
          sort: (saved.sort?.sortModel || []).map(col => ({
            column: fieldName(col.colId),
            dir: col.sort
          })),
          filters: Object.fromEntries(
            Object.entries(saved.filter?.filterModel || {}).map(([field, filter]) => [
              fieldName(field),
              filter
            ])
          )
        }
  return {
    version: 1,
    hidden: Array.isArray(state.hidden) ? state.hidden.slice() : [],
    order: Array.isArray(state.order) ? state.order.slice() : [],
    widths:
      state.widths && typeof state.widths === 'object' && !Array.isArray(state.widths)
        ? { ...state.widths }
        : {},
    groups: Array.isArray(state.groups) ? state.groups.slice() : defaults.slice(),
    sort: Array.isArray(state.sort) ? state.sort.slice() : [],
    filters:
      state.filters && typeof state.filters === 'object' && !Array.isArray(state.filters)
        ? { ...state.filters }
        : {}
  }
}

export function matchesFilter (value, filter) {
  if (filter.conditions) {
    const results = filter.conditions.map(condition => matchesFilter(value, condition))
    return filter.operator === 'OR' ? results.some(Boolean) : results.every(Boolean)
  }
  if (filter.filterType === 'set') { return filter.values.some(item => String(item ?? '') === String(value ?? '')) }
  const blank = value === null || value === undefined || value === ''
  if (filter.type === 'blank') return blank
  if (filter.type === 'notBlank') return !blank
  if (blank) return false
  let actual = value
  let expected = filter.filter
  let end = filter.filterTo
  if (filter.filterType === 'date') {
    actual = new Date(value * 1000).toISOString().slice(0, 10)
    expected = (filter.dateFrom || '').slice(0, 10)
    end = (filter.dateTo || '').slice(0, 10)
  } else if (filter.filterType === 'number') {
    actual = Number(value)
    expected = Number(expected)
    end = Number(end)
  } else {
    actual = String(value).toLocaleLowerCase()
    expected = String(expected ?? '').toLocaleLowerCase()
  }
  switch (filter.type) {
    case 'equals':
      return actual === expected
    case 'notEqual':
      return actual !== expected
    case 'contains':
      return String(actual).includes(expected)
    case 'notContains':
      return !String(actual).includes(expected)
    case 'startsWith':
      return String(actual).startsWith(expected)
    case 'endsWith':
      return String(actual).endsWith(expected)
    case 'lessThan':
      return actual < expected
    case 'lessThanOrEqual':
      return actual <= expected
    case 'greaterThan':
      return actual > expected
    case 'greaterThanOrEqual':
      return actual >= expected
    case 'inRange':
      return actual >= expected && actual <= end
    default:
      return true
  }
}

export function formatValue (value, column) {
  if (value === null || value === undefined) return ''
  if (column.field === 'conversion_ratio') return `${value}%`
  if (column.type === 'date') return new Date(value * 1000).toISOString().slice(0, 10)
  return String(value)
}

export function csvFor (columns, rows) {
  const escape = value => {
    // Excel must treat user supplied names as text, rather than executable formulas.
    const text =
      typeof value === 'string' && /^[\s]*[=+@-]/.test(value) ? `'${value}` : String(value ?? '')
    return `"${text.replaceAll('"', '""')}"`
  }
  return (
    '\uFEFF' +
    [
      columns.map(col => escape(col.title)).join(','),
      ...rows.map(row =>
        columns
          .map(col =>
            escape(col.type === 'date' ? formatValue(row[col.field], col) : row[col.field])
          )
          .join(',')
      )
    ].join('\r\n')
  )
}
